'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import LoadingButton from '@mui/lab/LoadingButton'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Link from '@mui/material/Link'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useTranslations } from 'next-intl'
import { useRouter, useSearchParams } from 'next/navigation'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import toast from '~/app/_components/toast'
import { useChallenge } from '~/app/sign/_hooks/use_challenge'
import client from '~/lib/client/client'
import CodeError from '~/lib/client/error'

// Define the schema for form validation
const schema = z.object({
  email: z.string().email({ message: 'Invalid email address' }),
})

export default function Page() {
  const t = useTranslations('auth.email')
  const router = useRouter()
  const params = useSearchParams()

  const [loading, setLoading] = useState(false)
  const { challenge } = useChallenge()

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
  })

  const onSubmit = async (data: any, e: any) => {
    setLoading(true)

    const pow = await challenge()

    const [_, err] = await client({
      path: '/api/auth/flow/email',
      method: 'POST',
      body: {
        email: data.email,
        reason: params.get('reason'),
        ...pow,
      },
    })

    setLoading(false)

    if (err?.code == CodeError.code.NotFound) {
      setError('email', {
        message: t('errors.user_not_found'),
      })
      return
    }

    toast({
      type: 'success',
      message: t('success'),
    })

    router.push('/sign/in')
  }

  const reason = params.get('reason')

  return (
    <Box
      component='div'
      sx={{
        width: '100%',
        height: '100%',
        maxWidth: '350px',
        margin: '0 auto',
        py: 2,
      }}
    >
      <Typography
        variant='h4'
        sx={{ textAlign: 'center', mb: 1, color: '#f0f0f1', fontWeight: 700 }}
      >
        {reason && t(`title.${reason}`)}
      </Typography>

      <Typography
        variant='body2'
        sx={{ textAlign: 'center', color: 'rgba(255, 255, 255, 0.6)', mb: 3 }}
      >
        {reason && t(`description.${reason}`)}
      </Typography>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container rowSpacing={2} columnSpacing={2}>
          <Grid size={{ xs: 12 }}>
            <Typography
              variant='body2'
              sx={{ color: '#f0f0f1', fontWeight: 500, mb: 1 }}
            >
              {t('form.email.label')}
            </Typography>
            <TextField
              placeholder={t('form.email.placeholder')}
              sx={{ width: '100%' }}
              {...register('email')}
              error={!!errors.email}
              helperText={errors.email?.message?.toString() || ''}
              autoComplete='email'
            />
          </Grid>
        </Grid>

        <LoadingButton
          variant='contained'
          type='submit'
          fullWidth
          sx={{
            my: 2,
            py: 1,
            background: loading
              ? '#1a1a1a'
              : 'linear-gradient(135deg, #27B1C6 0%, #59CDA1 100%)',
            boxShadow: loading ? 'none' : '0 2px 8px rgba(39, 177, 198, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #2ed3ed 0%, #6df6c1 100%)',
              boxShadow: '0 4px 12px rgba(39, 177, 198, 0.4)',
            },
            fontWeight: 600,
            fontSize: '0.875rem',
          }}
          loading={loading}
        >
          {t('form.submit')}
        </LoadingButton>

        <Box sx={{ textAlign: 'center' }}>
          <Link href='/sign/in' sx={{ fontSize: '14px' }}>
            {t('form.back_to_login')}
          </Link>
        </Box>
      </form>
    </Box>
  )
}
